import type { MaterialSymbol } from "material-symbols";
import { type FC, forwardRef, type ReactNode, type RefObject, useRef } from "react";

import { cn } from "../../utils";
import { AutoresizingTextarea, type AutoresizingTextareaProps } from "../autoresizing-textarea";
import { Button, type ButtonProps } from "../button";
import { Icon } from "../icon";
import { Spinner } from "../loading";

export interface NoteInputProps extends AutoresizingTextareaProps {
  icon?: MaterialSymbol;
  name: string;
  loading?: boolean;
  submitOnEnter?: boolean;
  value?: string;
  submitButtonProps?: NoteInputSubmitButtonProps;
  leftContent?: ReactNode; // Optional content for left side (e.g., recording controls)
}

export const NoteInput = forwardRef<HTMLTextAreaElement, NoteInputProps>(
  (
    { icon = "send", loading, submitOnEnter = false, className, submitButtonProps, leftContent, onKeyDown, ...props },
    ref
  ) => {
    const submitButton = useRef<HTMLButtonElement>(null);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Call the custom onKeyDown handler first if provided
      onKeyDown?.(event);

      // Then handle submit on enter
      if (submitOnEnter && event.key === "Enter" && !event.shiftKey && !event.defaultPrevented) {
        event.preventDefault();

        if (props.value?.trim()) {
          submitButton.current?.click();
        }
      }
    };

    return (
      <AutoresizingTextarea
        {...props}
        ref={ref}
        onKeyDown={handleKeyDown}
        className={cn(
          "focus-visible:ring-offset-0 min-h-10 py-[11px] pr-11 rounded-md text-xs",
          leftContent ? "pl-11" : "pl-3",
          className
        )}
      >
        {leftContent && <div className="absolute top-1 left-1 flex items-center gap-1">{leftContent}</div>}
        <NoteInputSubmitButton ref={submitButton} loading={loading} icon={icon} {...submitButtonProps} />
      </AutoresizingTextarea>
    );
  }
);

NoteInput.displayName = "NoteInput";

export interface NoteInputSubmitButtonProps extends ButtonProps {
  icon?: MaterialSymbol;
  submitOnEnter?: boolean;
  loading?: boolean;
  ref?: RefObject<HTMLButtonElement | null>;
}

export const NoteInputSubmitButton: FC<NoteInputSubmitButtonProps> = ({
  ref,
  loading,
  icon = "send",
  submitOnEnter,
  className,
  ...props
}) => (
  <Button
    ref={ref}
    type="submit"
    variant="ghost"
    theme="primary"
    size="sm"
    display="icon"
    {...props}
    className={cn("absolute top-1 right-1", className)}
  >
    {loading ? <Spinner /> : <Icon icon={icon} />}
  </Button>
);
